import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Ticket {
  id: string;
  clientId: string;
  status: string;
  severity: string;
  userComplaint: string;
  userFacingSummary: string;
  adminDetail: Record<string, unknown>;
  resolutionNote: string | null;
  conversationRef: string;
  createdAt?: { seconds: number };
  acknowledgedBy?: string | null;
}

interface Props {
  ticket: Ticket;
  onChange: () => void;
}

export function SupportTicketRow({ ticket, onChange }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [busy, setBusy] = useState(false);

  const call = async (name: string, data: unknown) => {
    setBusy(true);
    try {
      await httpsCallable(functions, name)(data);
      onChange();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = () =>
    call('acknowledgeTicket', { ticketId: ticket.id, status: 'acknowledged' });
  const investigate = () =>
    call('acknowledgeTicket', { ticketId: ticket.id, status: 'investigating' });
  const resolve = () => {
    if (!resolutionNote.trim()) {
      alert('Resolution note required.');
      return;
    }
    call('resolveTicket', { ticketId: ticket.id, resolutionNote });
  };

  const severityColor: Record<string, string> = {
    low: '#6b7280',
    medium: '#d97706',
    high: '#dc2626',
    urgent: '#7c2d12',
  };

  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 4,
            background: severityColor[ticket.severity] ?? '#555',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {ticket.severity?.toUpperCase() ?? 'N/A'}
        </span>
        <code style={{ color: 'var(--gold-light)' }}>{ticket.id}</code>
        <span style={{ opacity: 0.6 }}>
          {ticket.createdAt ? new Date(ticket.createdAt.seconds * 1000).toLocaleString() : ''}
        </span>
        <span style={{ flex: 1, paddingLeft: 10 }}>{ticket.userComplaint?.slice(0, 100)}</span>
        <span style={{ opacity: 0.7 }}>{ticket.status}</span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="btn btn-secondary"
          style={{ padding: '4px 10px' }}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <p>
            <b>Client:</b> <code>{ticket.clientId}</code>
          </p>
          <p>
            <b>User-facing summary shown to client:</b>
          </p>
          <div style={{ background: '#111', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
            {ticket.userFacingSummary}
          </div>

          <p>
            <b>Full user complaint:</b>
          </p>
          <div style={{ background: '#111', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
            {ticket.userComplaint}
          </div>

          <p>
            <b>Admin diagnostic detail:</b>
          </p>
          <pre
            style={{
              background: '#111',
              padding: 10,
              borderRadius: 6,
              overflow: 'auto',
              fontSize: 12,
            }}
          >
            {JSON.stringify(ticket.adminDetail, null, 2)}
          </pre>

          {ticket.resolutionNote && (
            <>
              <p>
                <b>Resolution note:</b>
              </p>
              <div style={{ background: '#0d2818', padding: 10, borderRadius: 6 }}>
                {ticket.resolutionNote}
              </div>
            </>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ticket.status === 'open' && (
              <button onClick={acknowledge} disabled={busy} className="btn btn-secondary">
                Acknowledge
              </button>
            )}
            {(ticket.status === 'open' || ticket.status === 'acknowledged') && (
              <button onClick={investigate} disabled={busy} className="btn btn-secondary">
                Mark Investigating
              </button>
            )}
            {ticket.status !== 'resolved' && (
              <>
                <input
                  type="text"
                  placeholder="Resolution note (shown to client)"
                  value={resolutionNote}
                  onChange={e => setResolutionNote(e.target.value)}
                  style={{ flex: 1, minWidth: 260, padding: '6px 10px' }}
                />
                <button onClick={resolve} disabled={busy} className="btn btn-primary">
                  Mark Resolved
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
